import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { CreditCardApplicationContext } from '../../../CreditCardApplicationContext';
import Card, {
  ErrorCard,
  LoadingCard,
  RetryWorkflowEventCard,
} from 'src/commons/components/Card';
import { Carousel } from 'src/commons/components/Carousel';
import Heading from 'src/commons/components/Heading';
import {
  LoadingRing,
  MiniLoadingRing,
} from 'src/commons/components/LoadingRing';
import {
  MOBILE_BREAKPOINT,
  CARD_BREAKPOINT,
  colors,
  PageNames,
  ApplicationWorkflowEvent,
  ApplicationStatus,
  GraphQLErrorCodes,
  UNAUTHORIZED_MESSAGE,
} from 'src/commons/constants';
import { processCreditCardApplicationWorkflowEvent } from 'src/commons/data/queries';
import {
  logError,
  nrTrackCreditCardApplicationWorkflowEvent,
} from 'src/commons/utilities/logging';
import { prefixRoute } from 'src/commons/utilities/routes';
import {
  useApplicationPolling,
  PollingStatus,
} from 'src/commons/utilities/useApplicationPolling';

// VerificationWaitingRoom
// Poll the application while the status is VERIFICATION_IN_PROGRESS
// Show a LoadingRing while polling app once per second with a timeout of 90 seconds
// Route to the AutoPay page when PENDING_SUBMISSION comes through successfully
// Display RetryWorkflowEventCard if VERIFICATION_ERROR occurs
// Display the ErrorModal when timeout occurs

// Possible statuses at conclusion of app polling:
//  - PENDING_SUBMISSION       : Route to AutoPay page
//  - VERIFICATION_ERROR       : Show RetryWorkflowEventCard and allow VERIFICATION_RETRIED
//  - VERIFICATION_IN_PROGRESS : Route to VerificationEmail page

// Constants --------------------------------------------------------------------------------------
const POLLING_TIMEOUT = 90000; // maximum time in ms to poll the application
const AUTO_PAY_REDIRECT_DELAY = 1000; // delay before redirecting to AutoPay on PENDING_SUBMISSION

// Styles -----------------------------------------------------------------------------------------
const WaitingCard = styled(Card)`
  padding: 4rem;
  @media (max-width: ${CARD_BREAKPOINT}) {
    padding: 3.4375rem 2rem;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 3.4375rem 1.25rem;
  }
`;
WaitingCard.displayName = 'WaitingCard';

const WaitingHeading = styled(Heading)`
  margin: 2rem 0 0;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 1.25rem 0 0;
  }
`;
WaitingHeading.displayName = 'WaitingHeading';

const WaitingParagraph = styled.p`
  margin: 1rem 4rem 0;
  line-height: 1.5;
  font-size: 0.875rem;
  text-align: center;
  color: ${colors.TEXT_PRIMARY};
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    max-width: 19.6875rem;
    margin: 1.25rem 0 1.8125rem;
  }
`;
WaitingParagraph.displayName = 'WaitingParagraph';

const MiniLoadingCircleContainer = styled.div`
  margin: 0 2rem 0 0;
  display: flex;
  align-items: center;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 0.5rem 0 0.5rem 0.75rem;
  }
`;
MiniLoadingCircleContainer.displayName = 'MiniLoadingCircleContainer';

const MiniLoadingLabel = styled.p`
  margin: 0 0 0 0.375rem;
  font-size: 0.75rem;
  color: ${colors.TEXT_PRIMARY};
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 0 0 0 0.75rem;
    font-size: 0.875rem;
  }
`;
MiniLoadingLabel.displayName = 'MiniLoadingLabel';

const MiniLoadingCircleRow = styled.div`
  max-width: 100%;
  margin: 2.5rem auto;
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  ${MiniLoadingCircleContainer}:last-child {
    margin-right: 0;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 0;
    flex-flow: column nowrap;
    align-items: flex-start;
  }
`;
MiniLoadingCircleRow.displayName = 'MiniLoadingCircleRow';

const carouselCards = [
  {
    id: 'shieldLockIconSVG',
    image: '../../../assets/icons/shield-lock-icon.svg',
    label:
      'Your Prosper<sup>®</sup> Card comes with bedrock technology designed to protect you and your data from fraud.',
    shieldImage: true,
  },
  {
    id: 'lightningIconSVG',
    image: '../../../assets/icons/lightning-icon.svg',
    label:
      "With your Prosper<sup>®</sup> Card, you'll get access to educational courses and resources—100% free!",
  },
  {
    id: 'heartIconSVG',
    image: '../../../assets/icons/heart-icon.svg',
    label:
      "We're trusted by our customers. Prosper Marketplace has a 4.8/5 rating from over 3k reviews on Trustpilot<sup>®</sup>",
  },
  {
    id: 'ideaIconSVG',
    image: '../../../assets/icons/idea-icon.svg',
    label:
      'With the Prosper<sup>®</sup> Card, we won’t charge you fees for ATM cash withdrawals.',
  },
  {
    id: 'keyIconSVG',
    image: '../../../assets/icons/key-icon.svg',
    label:
      "We'll automatically review your account for credit line increases up to a total line of $3,000!¹",
  },
];

export const VerificationWaitingRoom = () => {
  // Context --------------------------------------------------------------------------------------
  const {
    creditCardApplication,
    fetchCreditCardApplication,
    fetchAndResumeCreditCardApplication,
    fetchingApplication,
    showVerificationWaitingRoom,
    setShowVerificationWaitingRoom,
  } = useContext(CreditCardApplicationContext);

  // State ----------------------------------------------------------------------------------------
  const [loading, setLoading] = useState(true); // true while animating the loading rings
  const [polling, setPolling] = useState(true); // true while polling application
  const [success, setSuccess] = useState(false); // true when PENDING_SUBMISSION
  const [retry, setRetry] = useState(false); // show Retry component if VERIFICATION_ERROR
  const [error, setError] = useState(false); // show error if networking failure occurs
  const [timedOut, setTimedOut] = useState(false); // route to VerificationEmail page on timeout
  const [stepsComplete, setStepsComplete] = useState(0); // number of mini rings filled

  // Hooks ----------------------------------------------------------------------------------------
  const { applicationId } = useParams();
  const [appPollState] = useApplicationPolling(applicationId, polling);

  // Route to AutoPay upon successful cash flow verification
  // Route to VerificationEmail upon polling timeout
  const navigate = useNavigate();

  // Pass to RetryWorkflowEventCard to allow VERIFICATION_RETRIED upon VERIFICATION_ERROR
  const retryVerification = async () => {
    try {
      const verificationRetriedEvent =
        ApplicationWorkflowEvent.VERIFICATION_RETRIED;
      const response = await processCreditCardApplicationWorkflowEvent(
        applicationId,
        verificationRetriedEvent
      );
      const { data, errors } = response;
      nrTrackCreditCardApplicationWorkflowEvent(data);
      if (data && data.processCreditCardApplicationWorkflowEvent) {
        // Reset retry, reset timeout, and restart polling if workflow event was successful
        setRetry(false);
        setTimedOut(true);
        setPolling(true);
      } else if (errors && errors.length > 0 && errors[0].code) {
        // Unexpected status change - redirect accordingly
        switch (errors[0].code) {
          case GraphQLErrorCodes.INVALID_WORKFLOW_EVENT_FOR_STATUS:
          case GraphQLErrorCodes.TERMINAL_APPLICATION:
            // Set loading to false if error code indicates invalid status for this event
            setLoading(false);
            break;
          default:
            setError(true);
        }
      } else {
        setError(true);
      }
    } catch (err: any) {
      if (err.message !== UNAUTHORIZED_MESSAGE) {
        setError(true);
        logError(err, {
          errorName: err.name || 'PageError',
          errorMessage: err.message || 'Unexpected error occurred',
          component: 'VerificationWaitingRoom',
          function: 'retryVerification',
        });
      }
    }
  };

  useEffect(() => {
    fetchCreditCardApplication();
  }, []);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    // Wait for the application to become available from context
    if (!fetchingApplication) {
      const { applicationStatus } = creditCardApplication;

      // Verify the application status is within the verification flow
      if (applicationStatus === ApplicationStatus.VERIFICATION_ERROR) {
        // Reset showVerificationWaitingRoom so the user is not shown VerificationEmail upon retry
        setShowVerificationWaitingRoom(true);
        // Show RetryWorkflowEventCard if the application status is VERIFICATION_ERROR
        setRetry(true);
        setPolling(false);
      } else if (
        applicationStatus === ApplicationStatus.VERIFICATION_IN_PROGRESS
      ) {
        if (showVerificationWaitingRoom) {
          // Indicate in context this page should not be shown if page is refreshed
          setShowVerificationWaitingRoom(false);
        } else {
          // Redirect to VerificationEmail if user did not retry or just ConnectToBank
          setTimedOut(true);
          setPolling(false);
        }
      }
    }
    return () => clearTimeout(timer);
  }, [fetchingApplication]);

  // Route to AutoPay page if polling concludes with PENDING_SUBMISSION
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (success && !loading) {
      // Route to AutoPay after a one second delay
      timer = setTimeout(() => {
        // Replace rather than push so the user cannot return by clicking Back in the browser
        navigate(prefixRoute(`/application/${applicationId}/auto-pay`), {
          replace: true,
        });
      }, AUTO_PAY_REDIRECT_DELAY);
    } else if (!loading) {
      // Update application in context if status does not correspond to this page
      fetchAndResumeCreditCardApplication(PageNames.VERIFICATION_WAITING_ROOM);
    }
    return () => clearTimeout(timer);
  }, [loading]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (!timedOut) {
      // Start/restart timeout
      timer = setTimeout(() => {
        setTimedOut(true);
        setPolling(false);
      }, POLLING_TIMEOUT);
    }
    return () => clearTimeout(timer);
  }, [timedOut]);

  // Allow display of verification waiting room upon retry
  useEffect(() => {
    if (retry) {
      setShowVerificationWaitingRoom(true);
    }
  }, [retry]);

  // Poll application - timeout after max time elapses and show ErrorModal
  // Reset timeout if VERIFICATION_ERROR occurs and allow retry via RetryWorkflowEventCard
  useEffect(() => {
    if (polling && timedOut && !retry && !success) {
      // User attempted retry via VERIFICATION_RETRIED workflow event - reset timeout
      setTimedOut(false);
    } else if (!polling && timedOut && !retry) {
      // Redirect to VerificationEmail page if timeout occurs without status transition
      // Replace rather than push so the user cannot return by clicking Back in the browser
      navigate(
        prefixRoute(`/application/${applicationId}/verification-email`),
        { replace: true }
      );
    } else if (!polling && !retry && !success) {
      // Redirect to appropriate funnel page after an unexpected status is polled
      setShowVerificationWaitingRoom(false);
      fetchAndResumeCreditCardApplication(PageNames.VERIFICATION_WAITING_ROOM);
    }
  }, [polling]);

  // Continue polling application until status transitions from VERIFICATION_IN_PROGRESS
  useEffect(() => {
    // Check application status while polling is underway
    if (appPollState.pollingStatus === PollingStatus.ACTIVE) {
      const { application } = appPollState;
      const { applicationStatus } = application;

      switch (applicationStatus) {
        case ApplicationStatus.PENDING_SUBMISSION:
          // Happy Path - Stop polling when verification concludes with approval
          setSuccess(true);
          setPolling(false);
          break;
        case ApplicationStatus.VERIFICATION_ERROR:
          // Verification Failure - Stop polling and show RetryWorkflowEventCard
          setRetry(true);
          setPolling(false);
          break;
        case ApplicationStatus.VERIFICATION_IN_PROGRESS:
          // In Progress - Continue polling until application status changes
          break;
        default:
          // Unexpected Status - Stop polling and redirect accordingly
          setPolling(false);
          break;
      }
    } else if (appPollState.pollingStatus === PollingStatus.FAILURE) {
      // Stop polling if an error occurred fetching the application
      setError(true);
      setPolling(false);
    }
  }, [appPollState]);

  if (fetchingApplication) {
    return <LoadingCard />;
  }

  // Show RetryWorkflowEventCard when status VERIFICATION_ERROR is polled
  if (retry) {
    return <RetryWorkflowEventCard onRetry={() => retryVerification()} />;
  }

  if (error) {
    return <ErrorCard />;
  }

  if (timedOut) {
    return <WaitingCard id="timed-out-blank-card" />;
  }

  return (
    <WaitingCard
      id="verification-waiting-room-card"
      aria-labelledby="verification-waiting-room-heading"
      aria-describedby="verification-waiting-room-message"
    >
      <LoadingRing
        id="verification-waiting-room-loading-ring"
        completionCallback={() => setLoading(false)}
        allowCompletion={stepsComplete === 3 && success}
      />
      <WaitingHeading id="verification-waiting-room-heading" role="alert">
        {stepsComplete === 3 && success
          ? 'Complete!'
          : 'Hold tight, verifying...'}
      </WaitingHeading>
      <WaitingParagraph id="verification-waiting-room-message">
        This process usually takes around 90 seconds. Please do not refresh or
        close your browser during this process.
      </WaitingParagraph>
      <MiniLoadingCircleRow id="mini-loading-circles">
        <MiniLoadingCircleContainer id="loading-account-holder">
          <MiniLoadingRing
            id="mini-loading-ring-account-holder"
            completionCallback={() => setStepsComplete(1)}
            start={stepsComplete === 0}
            completed={stepsComplete > 0}
            allowCompletion={success}
          />
          <MiniLoadingLabel id="account-holder-text">
            Account holder
          </MiniLoadingLabel>
        </MiniLoadingCircleContainer>
        <MiniLoadingCircleContainer id="loading-cash-positive-balance">
          <MiniLoadingRing
            id="mini-loading-ring-cash-positive-balance"
            completionCallback={() => setStepsComplete(2)}
            start={stepsComplete === 1}
            completed={stepsComplete > 1}
            allowCompletion={stepsComplete === 1 && success}
          />
          <MiniLoadingLabel id="cash-positive-balance-text">
            Cash-positive balance
          </MiniLoadingLabel>
        </MiniLoadingCircleContainer>
        <MiniLoadingCircleContainer id="loading-regular-deposits">
          <MiniLoadingRing
            id="mini-loading-ring-cash-regular-deposits"
            completionCallback={() => setStepsComplete(3)}
            start={stepsComplete === 2}
            completed={stepsComplete > 2 && success}
            allowCompletion={stepsComplete === 2 && success}
          />
          <MiniLoadingLabel id="regular-deposits-text">
            Regular deposits
          </MiniLoadingLabel>
        </MiniLoadingCircleContainer>
      </MiniLoadingCircleRow>
      <Carousel cards={carouselCards} />
    </WaitingCard>
  );
};
